import { useContext, useEffect, useState, useRef, Fragment } from "react";

import TextInput from "../text-input/text-input";
import "./search-input.scss";
import { IEnumerationOption } from "../../definitions/datatypes";
import AuthContext from "../../../shared/context/auth-context";
import { getAllData } from "../../../shared/utils/api";
import useHttp from "../../../shared/hooks/use-http";
import { ITableRowData } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";

import { BsSearch } from "react-icons/bs";

export interface IProps {
  inputKey: string;
  label?: string;
  required: boolean;
  dataType: string;
  data: IEnumerationOption[];
  id?: string;
  mode?: string;
  noRoleCheck?: boolean;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  extraId?: string;
  autocompleteOff?: boolean;
  showInputIcons?: JSX.Element;
  onFocusEmpty?: boolean;
  extraClass?: string;
  extraToolTipInfo?: string;
  withInnerHeight?: string;
  generateUniqueId?: boolean;
  onParentActionCloseResult?: boolean;
  parentId?: string;
  handleOnChange(newValue: string, key: string, id?:string, extraId?: string, parentId?: string): void;
  getData?: (url: string) => void;
}

interface OutsideClickHandlerProps {
  ref: React.RefObject<HTMLElement>;
  handler: () => void;
}

const useOutsideClick = ({ ref, handler }: OutsideClickHandlerProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, handler]);
};

const SearchInput = ({ inputKey, generateUniqueId, parentId, extraToolTipInfo,  onParentActionCloseResult, onFocusEmpty, withInnerHeight, extraId, noRoleCheck, extraClass, showInputIcons, autocompleteOff, required, placeholder, disabled, id, getData, mode, dataType, handleOnChange, label, defaultValue, data }: IProps) => {
  const [inputVal, setInputVal] = useState("");
  const [lastSelectedData, setLastSelectedData] = useState({newVal : "", key: "", id: ""});
  const [showList, setShowList] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<IEnumerationOption[]>([]);
  const [allOptions, setAllOptions] = useState<IEnumerationOption[]>([]);

  const authCtx = useContext(AuthContext);
  const { sendRequest: getSingleEntity, status: allStatusSingle, data: loadedDataSingle, error: errorGetSingle } = useHttp(getAllData, false, authCtx.token, "entity");
  const { sendRequest: getAllDataRequest, status: allStatus, data: loadedData, error: errorGet } = useHttp(getAllData, false, authCtx.token);

  const resultRef = useRef<HTMLDivElement | null>(null);
  const scroller = withInnerHeight? document.querySelector(withInnerHeight) : 0;

  useOutsideClick({
    ref: resultRef,
    handler: () => setShowList(false)
  });

  useEffect(() => {
    setShowList(false);
  }, [onParentActionCloseResult]);

  useEffect(() => {
    if (dataType === "reference" && inputKey === "referencing_component" || inputKey==="referencing_component_field") {
      setAllOptions([]);
      if (
        (inputKey === "referencing_component" && id && id !== "referencing_component") ||
        (inputKey==="referencing_component_field" && id && id !== "referencing_component_field" && id !== "referencing_component")
      ) {
        getSingleEntity(`datamodel/entity/${id}`);
      } else if (inputKey === "referencing_component" && id) {
        getAllDataRequest("datamodel/entities","?archived=false");
      }
    } else if (dataType === "enumeration") {
      getAllDataRequest("datamodel/enumerations");
    } else if (inputKey === "event_name" && data.length === 0) {
      getData && getData("events");
    }
  }, [dataType, inputKey, id]);

  useEffect(() => {
    if (allStatus === "completed" && !errorGet) {
      loadedData.rows && setAllOptions(loadedData.rows); 
    } else if (allStatus === "completed" && errorGet) {
      loadedData.rows && setAllOptions([]);
    }
  }, [allStatus]);

  useEffect(() => {
    if (allStatusSingle === "completed" && !errorGetSingle) {
      const result = loadedDataSingle && loadedDataSingle.rowData.map((row: ITableRowData) =>  ({id: row.id, name: row.field_name}));
      result && setAllOptions(prevState => ([ ...prevState, ...result ]));
    }
  }, [allStatusSingle]);

  useEffect(() => {
    if (data) {
      data.length !== 0 && setAllOptions(data);
    }
  }, [data]);

  useEffect(() => {
    if (defaultValue) {
      setInputVal(defaultValue);
    } else {
      setInputVal("");
    }
  }, [defaultValue]);

  useEffect(() => {
    if (allOptions.length !== 0) {
      const results = allOptions.filter((opt) =>
        opt.name.toLowerCase().includes(inputVal.toLowerCase())
      );
      setFilteredOptions(results);
    } else {
      setFilteredOptions([]);
    }
  }, [inputVal, allOptions, data]);

  useEffect(() => {
    if (allOptions.length !== 0 && id) {
      const findTheTenantById = allOptions.find((option) => id === option.id);
      findTheTenantById && setLastSelectedData({newVal:findTheTenantById.name, key: inputKey, id: findTheTenantById.id});
    } 
  }, [allOptions]);

  const handleInputChange = (newVal: string, key: string, id: string) => {
    setInputVal(newVal);
    if (newVal === "") {
      setShowList(true);
    }
    // if user provided identical option names and did not click on the list item to select one,but typed in manually, then select first found match
    const checkManualTyping = filteredOptions.filter((option) => newVal === option.name);
    if (checkManualTyping.length !== 0) {   
      setLastSelectedData({newVal, key, id: checkManualTyping[0].id});
    } else {
      handleOnChange(newVal, key, id, extraId, parentId);
    }
  };

  const handleKeyDown = (event:React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onActionHandler();
    }
  };

  const onActionHandler = () => {
    if (allOptions.length === 0 || (!inputVal && !lastSelectedData.id)) {
      setInputVal("");
      return;
    }
    if (lastSelectedData.id && inputVal !== "") {
      // lastSelectedData exists
      handleOnChange(lastSelectedData.newVal, lastSelectedData.key, lastSelectedData.id, extraId, parentId);
    } else if (allOptions.length !== 0 && !lastSelectedData.id && inputVal !== "") {
      allOptions && handleOnChange(allOptions[0].name, inputKey, allOptions[0].id, extraId, parentId);
      allOptions && setLastSelectedData({newVal : allOptions[0].name, key: inputKey, id :allOptions[0].id});
      setInputVal(allOptions[0].name);
    }
    else if (inputVal === "") {
      if (allOptions.length !== 0 && !autocompleteOff && !lastSelectedData.id) {
        // lastSelectedData doesnt exist
        allOptions && handleOnChange(allOptions[0].name, inputKey, allOptions[0].id, extraId, parentId);
        setInputVal(allOptions[0].name);
      } 
      else if (allOptions.length !== 0 && onFocusEmpty && lastSelectedData && required) {
        // lastSelectedData exists and onFocusEmpty is on
        handleOnChange(lastSelectedData.newVal, lastSelectedData.key, lastSelectedData.id, extraId, parentId);
        setInputVal(lastSelectedData.newVal);
      }
    }
  };

  const onFocusHandler = () => {
    setShowList(true);
    if (filteredOptions.length > 0) {
      onFocusEmpty && setInputVal("");
    }
  };

  return (
    <Fragment>
      <div ref={resultRef} className={`searchInput-${inputKey} ${mode} ${extraClass}`}>
        {validateLoadingStatusNew([allStatus, allStatusSingle]) && <Spinner />}
        <TextInput
          type={"text"}
          id={id ? id: inputKey}
          label={label}
          noRoleCheck={noRoleCheck} 
          mode={mode === "aside" ? "aside" : ""}
          required={required}
          inputKey={inputKey}
          generateUniqueId={generateUniqueId}
          autofocus={false}
          placeholder={placeholder ? placeholder : "Type to search"}
          handleOnFocus={() => onFocusHandler()}
          onKeyDown={handleKeyDown}
          handleOnBlur={() => onActionHandler()}
          handleOnChange={handleInputChange}
          value={inputVal}
          controlled={true}
          extraToolTipInfo={extraToolTipInfo}
          disabled={disabled} 
          name={inputKey}
        />
        {showList && filteredOptions.length !== 0 && (
          <div className="searchInput-resultWrapper">
            {withInnerHeight && scroller && resultRef?.current? <div style={{ top: resultRef?.current.offsetTop - (scroller?.scrollTop? scroller?.scrollTop : 0) + 40 }}  className={`searchInput-result ${mode === "aside" ? "aside" : ""} ${filteredOptions.length < 8? "rounded" : ""}`}>
              <ul>
                {filteredOptions.map((option, i) => (
                  <li
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setInputVal(option.name);
                      handleInputChange(option.name, inputKey, option.id);
                      handleOnChange(option.name, inputKey, option.id, extraId, parentId);
                      setShowList(false);
                    }}
                    key={i}
                  >
                    {option.name}
                  </li>
                ))}
              </ul>
            </div> : <div className={`searchInput-result ${mode === "aside" ? "aside" : ""} ${filteredOptions.length < 8? "rounded" : ""}`}>
              <ul>
                {filteredOptions.map((option, i) => (
                  <li
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setInputVal(option.name);
                      handleInputChange(option.name, inputKey, option.id);
                      handleOnChange(option.name, inputKey, option.id, extraId, parentId);
                      setShowList(false);
                    }}
                    key={i}
                  >
                    {option.name}
                  </li>
                ))}
              </ul>
            </div>}
          </div>
          
        )}
      </div>
      {showInputIcons && (showList? (
        <span className="searchInput-switchButon-search">
          <BsSearch />
        </span>
      ) : (
        <span
          onClick={() => {
            const tenantInput = id && document.getElementById(id);
            tenantInput && tenantInput.focus();
          }}
          className="searchInput-switchButon-org"
        >
          {showInputIcons}
        </span>
      ))}
    </Fragment>
  );
};

export default SearchInput;
